import React, { createContext, useMemo, type ReactNode } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from '@apollo/client/react'
import { GET_APP_CONFIG } from '../graphql/queries/appConfig'
import type { GetAppConfigQuery } from '../types/graphql-generated'

/**
 * Fallback market-hours window, used only while the backend value is loading or
 * if the query fails. The backend (`GameConstants`) is the source of truth, so
 * these defaults just keep the UI sensible during that brief window.
 * Format: "HH:MM" in 24-hour Madrid time.
 */
const DEFAULT_MARKET_CLOSE_TIME = '22:30'
const DEFAULT_MARKET_OPEN_TIME = '07:00'

/**
 * Fallback max length for a channel post, used only while the backend value is
 * loading or if the query fails. Mirrors the Post model's MAX_TEXT_LENGTH.
 */
const DEFAULT_POST_MAX_TEXT_LENGTH = 2000

/**
 * Fallback max length for a post's related-link title, used only while the
 * backend value is loading or if the query fails. Mirrors the Post model's
 * MAX_LINK_TITLE_LENGTH.
 */
const DEFAULT_POST_MAX_LINK_TITLE_LENGTH = 100

/**
 * Fallback max number of photos in a channel post, used only while the backend
 * value is loading or if the query fails. Mirrors the Post model's MAX_PHOTOS.
 */
const DEFAULT_POST_MAX_PHOTOS = 4

export interface MarketHours {
  /** Time the market closes (restriction starts), "HH:MM". */
  closeTime: string
  /** Time the market reopens (restriction ends), "HH:MM". */
  openTime: string
}

export interface AppConfigContextValue {
  marketHours: MarketHours
  /** Maximum length (characters) allowed for a channel post's text. */
  postMaxTextLength: number
  /** Maximum length (characters) allowed for a post's related-link title. */
  postMaxLinkTitleLength: number
  /** Maximum number of photos a channel post can have. */
  postMaxPhotos: number
}

// eslint-disable-next-line react-refresh/only-export-components
export const AppConfigContext = createContext<AppConfigContextValue | undefined>(undefined)

interface AppConfigProviderProps {
  children: ReactNode
}

export const AppConfigProvider: React.FC<AppConfigProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth0()

  const { data } = useQuery<GetAppConfigQuery>(GET_APP_CONFIG, {
    skip: authLoading || !isAuthenticated,
  })

  const value = useMemo<AppConfigContextValue>(
    () => ({
      marketHours: {
        closeTime: data?.appConfig.marketHours.startTime ?? DEFAULT_MARKET_CLOSE_TIME,
        openTime: data?.appConfig.marketHours.endTime ?? DEFAULT_MARKET_OPEN_TIME,
      },
      postMaxTextLength: data?.appConfig.postMaxTextLength ?? DEFAULT_POST_MAX_TEXT_LENGTH,
      postMaxLinkTitleLength:
        data?.appConfig.postMaxLinkTitleLength ?? DEFAULT_POST_MAX_LINK_TITLE_LENGTH,
      postMaxPhotos: data?.appConfig.postMaxPhotos ?? DEFAULT_POST_MAX_PHOTOS,
    }),
    [data]
  )

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>
}
